// Type-only imports: this module is also imported by Utils/auth.ts (logout),
// and a runtime import of useFocusMode would loop back to it through
// focusMode.api -> fetchClient -> Utils/auth.
import type { BoxSelection, BoxPathItem, TableauSelection, SalesforceSelection } from '../hooks/useFocusMode';

// The picks (sources, modes, Box/Tableau/Salesforce selections, chat and
// image models) that a chat's latest message was sent with, kept in this
// browser so reopening the chat — from history, or after a refresh — shows
// them again. One entry per chat, overwritten on every send, so only the
// latest picks are ever kept.
//
// localStorage rather than sessionStorage so it survives refresh and works
// in other tabs too. Cleared on logout (useLogout, Utils/auth's
// forceLogout) and whenever the login screen shows (LoginAuth); entries are
// also stamped with the user's email and never read back for a different
// user, which covers any sign-out path that doesn't clear them. A chat's
// entry also expires MAX_AGE_MS after its latest message.
export interface SavedChatSelections {
  // selectedSources (atlas/box/box-jobit/salesforce/tableau/jo-website/
  // image_generation)
  sources: string[];
  atlasMode: 'normal' | 'deep_insights';
  deepSearch: boolean;
  compose: boolean;
  webSearch: boolean;
  // Only whether File Upload was on — the files themselves are listed again
  // from the backend (they're stored there per chat).
  fileUpload: boolean;
  // Each model picker's pick (see useModelSelector) — kept whichever pickers
  // were showing. Entries saved before the image picker existed have no
  // imageModel (read back as null → the image default), and their model
  // may be an image model; restoring treats that like any model no longer
  // in the chat list, falling back to the chat default.
  model: string | null;
  imageModel: string | null;
  box: BoxSelection;
  tableau: TableauSelection;
  salesforce: SalesforceSelection;
}

interface StoredEntry extends SavedChatSelections {
  savedAt: number;
}

interface Store {
  version: number;
  owner: string;
  sessions: Record<string, StoredEntry>;
}

const STORAGE_KEY = 'chat_selections';
const STORAGE_VERSION = 1;
// Oldest chats beyond this are dropped. Each entry is well under 1 KB, so
// this stays far below the browser's storage limit.
const MAX_SAVED_CHATS = 200;
// How long after a chat's latest message its picks are still restored —
// past that, a Box file, Salesforce table or model may well have changed.
// Counted from the last send, not the last time the chat was opened.
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 1 day for now
const LOG_PREFIX = '[chat-selections]';

const normalizeOwner = (email: string) => email.trim().toLowerCase();

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const nonEmptyString = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

const readStore = (): Store | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      !isObject(parsed) ||
      parsed.version !== STORAGE_VERSION ||
      typeof parsed.owner !== 'string' ||
      !isObject(parsed.sessions)
    ) {
      return null;
    }
    return parsed as unknown as Store;
  } catch {
    return null;
  }
};

const writeStore = (store: Store) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (e) {
    console.warn(LOG_PREFIX, 'Could not save picks:', e);
  }
};

// An entry without a usable savedAt counts as expired too.
const isExpired = (entry: unknown, now: number) =>
  !isObject(entry) || typeof entry.savedAt !== 'number' || now - entry.savedAt > MAX_AGE_MS;

// Removes every expired entry from the store (in place) and returns the
// ids removed.
const dropExpired = (store: Store, now: number): string[] => {
  const expired = Object.keys(store.sessions).filter((id) => isExpired(store.sessions[id], now));
  expired.forEach((id) => { delete store.sessions[id]; });
  return expired;
};

// ── Validation ──────────────────────────────────────────────────────────────
// Everything read back is checked field by field — storage can hold an
// older shape or hand-edited data, and a bad value here must never reach
// the chat input's state.

const sanitizeBoxPath = (v: unknown): BoxPathItem[] =>
  Array.isArray(v)
    ? v.flatMap((p) => {
        if (!isObject(p)) return [];
        const id = nonEmptyString(p.id);
        return id ? [{ id, name: typeof p.name === 'string' ? p.name : '' }] : [];
      })
    : [];

const sanitizeBox = (v: unknown): BoxSelection => {
  if (isObject(v)) {
    if (v.type === 'all') return { type: 'all', id: null, name: null };
    const id = nonEmptyString(v.id);
    if ((v.type === 'file' || v.type === 'folder') && id) {
      return { type: v.type, id, name: nonEmptyString(v.name), path: sanitizeBoxPath(v.path) };
    }
  }
  return { type: 'none', id: null, name: null };
};

const sanitizeTableau = (v: unknown): TableauSelection => {
  if (isObject(v)) {
    if (v.type === 'all') return { type: 'all', id: null, name: null };
    const id = nonEmptyString(v.id);
    if (v.type === 'dashboard' && id) return { type: 'dashboard', id, name: nonEmptyString(v.name) };
  }
  return { type: 'none', id: null, name: null };
};

const sanitizeSalesforce = (v: unknown): SalesforceSelection => {
  if (isObject(v)) {
    if (v.type === 'all') return { type: 'all', id: null, name: null };
    const id = nonEmptyString(v.id);
    if (v.type === 'item' && id) {
      return { type: 'item', id, name: nonEmptyString(v.name), value: nonEmptyString(v.value) };
    }
  }
  return { type: 'none', id: null, name: null };
};

const sanitizeEntry = (v: unknown): SavedChatSelections | null => {
  if (!isObject(v)) return null;
  return {
    sources: Array.isArray(v.sources) ? v.sources.filter((s): s is string => typeof s === 'string') : [],
    atlasMode: v.atlasMode === 'deep_insights' ? 'deep_insights' : 'normal',
    deepSearch: v.deepSearch === true,
    compose: v.compose === true,
    webSearch: v.webSearch === true,
    fileUpload: v.fileUpload === true,
    model: nonEmptyString(v.model),
    imageModel: nonEmptyString(v.imageModel),
    box: sanitizeBox(v.box),
    tableau: sanitizeTableau(v.tableau),
    salesforce: sanitizeSalesforce(v.salesforce),
  };
};

// ── Public API ──────────────────────────────────────────────────────────────

// Saves (overwrites) the picks for one chat — called right before every send.
export const saveChatSelections = (ownerEmail: string, sessionId: string, selections: SavedChatSelections) => {
  const owner = normalizeOwner(ownerEmail);
  const existing = readStore();
  const store: Store =
    existing && existing.owner === owner
      ? existing
      : { version: STORAGE_VERSION, owner, sessions: {} };

  const now = Date.now();
  dropExpired(store, now);
  store.sessions[sessionId] = { ...selections, savedAt: now };

  const sessionIds = Object.keys(store.sessions);
  if (sessionIds.length > MAX_SAVED_CHATS) {
    const savedAt = (id: string) => (typeof store.sessions[id]?.savedAt === 'number' ? store.sessions[id].savedAt : 0);
    sessionIds
      .sort((a, b) => savedAt(b) - savedAt(a))
      .slice(MAX_SAVED_CHATS)
      .forEach((id) => { delete store.sessions[id]; });
  }

  writeStore(store);
};

// The picks saved for one chat, or null if there are none, they've expired
// (MAX_AGE_MS), or they belong to a different user — in which case
// everything stored is wiped. Expired entries are dropped while here.
export const loadChatSelections = (ownerEmail: string, sessionId: string): SavedChatSelections | null => {
  const store = readStore();
  if (!store) return null;
  if (store.owner !== normalizeOwner(ownerEmail)) {
    clearAllChatSelections();
    return null;
  }
  if (dropExpired(store, Date.now()).length > 0) writeStore(store);
  return sanitizeEntry(store.sessions[sessionId]);
};

// Drops one chat's entry — used when the chat itself is deleted.
export const removeChatSelections = (sessionId: string) => {
  const store = readStore();
  if (!store || !(sessionId in store.sessions)) return;
  delete store.sessions[sessionId];
  writeStore(store);
};

// Drops everything — used on logout.
export const clearAllChatSelections = () => {
  try {
    if (localStorage.getItem(STORAGE_KEY) === null) return;
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
};
